/* global globalThis */
import { getProductionStepId } from "./productionCardIdentity";

// Wait for reads to finish applying their results, not just their transports.
// Mutation refreshes are serialized; routine polls never cancel or adopt them.
export function createProductionMutationRefreshQueue() {
  const active = new Set();
  let tail = Promise.resolve();
  let queued = 0;
  return {
    begin() {
      let resolve;
      const done = new Promise((finish) => { resolve = finish; });
      active.add(done);
      return () => { active.delete(done); resolve(); };
    },
    busy() { return active.size > 0 || queued > 0; },
    run(task, isCurrent) {
      queued += 1;
      const result = tail.then(async () => {
        while (active.size) await Promise.all([...active]);
        return isCurrent() ? task() : false;
      });
      tail = result.catch(() => {}).finally(() => { queued -= 1; });
      return result;
    },
  };
}

export function getProductionScheduleKey(card) {
  if (!card) return "";
  return JSON.stringify([
    card.orderDbId ?? null, card.itemId ?? card.orderItemId ?? null,
    card.decorationId ?? null, card.external_key ?? card.externalkey ?? card.id,
    card.department ?? card.claimedDept ?? null,
  ]);
}

export function requirePersistedSchedule(result) {
  const stepId = Number(result?.step_id), pwoId = Number(result?.pwo_id);
  if (result?.success !== true || !Number.isSafeInteger(stepId) || stepId <= 0 ||
      !Number.isSafeInteger(pwoId) || pwoId <= 0) {
    throw new Error(result?.message || "The server did not confirm persisted step and work order identities. Refresh before retrying.");
  }
  return { id: String(stepId), step_id: stepId, stepid: stepId, pwo_id: pwoId, isStep: true };
}

// Display order numbers are not identities. Only a persisted step can displace
// itself from another lane; different steps/departments remain independent.
export function replaceProductionLane(columns, lane, cards) {
  const incoming = new Set(cards.map(getProductionStepId).filter(Boolean));
  const seen = new Set();
  const next = { ...columns };
  for (const key of Object.keys(next)) {
    if (key !== lane && key !== "Archive" && next[key].some((card) => incoming.has(getProductionStepId(card)))) {
      next[key] = next[key].filter((card) => !incoming.has(getProductionStepId(card)));
    }
  }
  next[lane] = cards.filter((card) => {
    const identity = getProductionStepId(card) || card.id;
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
  return next;
}

export function reconcileProductionStep(columns, card, lane, { sourceCard, beforeStepId, afterStepId } = {}) {
  const stepId = getProductionStepId(card);
  if (!stepId || !columns[lane] || lane === "Archive") return columns;
  const previousIndex = columns[lane].findIndex((row) => getProductionStepId(row) === stepId);
  const next = { ...columns };
  for (const key of Object.keys(next)) {
    if (key === "Archive") continue;
    next[key] = next[key].filter((row) => getProductionStepId(row) !== stepId &&
      !(sourceCard && !getProductionStepId(sourceCard) && !getProductionStepId(row) &&
        getProductionScheduleKey(row) === getProductionScheduleKey(sourceCard)));
  }
  const after = next[lane].findIndex((row) => getProductionStepId(row) === Number(afterStepId));
  const before = next[lane].findIndex((row) => getProductionStepId(row) === Number(beforeStepId));
  const index = after >= 0 ? after : before >= 0 ? before + 1 : previousIndex >= 0 ? previousIndex : next[lane].length;
  next[lane].splice(index, 0, { ...card, id: String(stepId), step_id: stepId, status: lane });
  return next;
}

export function getProductionHoldReturnLane(card) {
  return card?.status === "On_Hold" && ["Scheduled", "In_Production"].includes(card.holdFrom)
    ? card.holdFrom : null;
}

export function canDragProductionCard(card) {
  return Boolean(card && !card.cancelled && !card.isArchived && card.status !== "Archive" &&
    (card.status === "On_Hold" ? getProductionHoldReturnLane(card) && getProductionStepId(card) : !card.isPaused));
}

export function createProductionPendingMutations(instance, storage) {
  const key = `production:pending-mutations:${instance}`;
  let records = [];
  try {
    records = JSON.parse(storage?.getItem(key) || "[]").filter(record =>
      ["schedule", "complete"].includes(record.kind) && record.instance === instance &&
      (record.kind !== "complete" || (Number.isSafeInteger(record.step_id) && record.step_id > 0)) &&
      [record.orderDbId, record.itemId, record.decorationId].every(value => Number.isSafeInteger(value) && value > 0)
    ).map(record => ({ ...record, phase: "uncertain", existingSteps: Array.isArray(record.existingSteps)
      ? record.existingSteps.filter(value => Number.isSafeInteger(value) && value > 0) : [] }));
  } catch {}
  const persist = () => {
    try {
      // Persist identifiers only, never card/customer details, tokens or form data.
      storage?.setItem(key, JSON.stringify(records.filter(record => record.phase !== "failed" && record.kind !== "action").map(record => ({
        instance, kind: record.kind, key: record.key, orderDbId: record.orderDbId,
        itemId: record.itemId, decorationId: record.decorationId, step_id: record.step_id,
        department: record.department, from: record.from, to: record.to, existingSteps: record.existingSteps, mutationId: record.mutationId,
      }))));
    } catch {}
  };
  const matches = (record, card) => record.kind === "complete" || record.step_id
    ? Number(record.step_id) === getProductionStepId(card)
    : (!getProductionStepId(card) || !(record.existingSteps || []).includes(getProductionStepId(card))) && Number(card?.orderDbId) === record.orderDbId &&
      Number(card?.itemId ?? card?.orderItemId) === record.itemId && Number(card?.decorationId) === record.decorationId &&
      String(card?.department || card?.claimedDept || record.department).toLowerCase() === String(record.department).toLowerCase();
  return {
    instance,
    records: () => records,
    find: card => records.find(record => matches(record, card) &&
      !(record.phase === "failed" && (card?.status !== record.from || getProductionStepId(card) !== record.step_id))),
    begin(card, kind, { from = card.status, to, department = card.department, scope, existingSteps = [] } = {}) {
      const previous = records.find(record => matches(record, card));
      if (previous && previous.phase !== "failed") return null;
      if (previous) records = records.filter(record => record !== previous);
      const record = { instance, kind, key: `${kind}|${getProductionScheduleKey(card)}|${getProductionStepId(card) || ""}`,
        mutationId: globalThis.crypto.randomUUID ? globalThis.crypto.randomUUID() :
          Array.from(globalThis.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, '0')).join(''),
        orderDbId: Number(card.orderDbId), itemId: Number(card.itemId ?? card.orderItemId),
        decorationId: Number(card.decorationId), department, step_id: getProductionStepId(card),
        from, to, phase: "pending", sourceCard: card, scope, existingSteps };
      records.push(record); persist(); return record;
    },
    update(record, phase, error) { Object.assign(record, { phase, error }); persist(); },
    remove(record) { records = records.filter(value => value !== record); persist(); },
  };
}

export function protectPendingProductionColumns(columns, records, scope) {
  let next = columns;
  for (const record of records) {
    if (record.kind === "action" || record.phase === "failed" || !record.sourceCard || record.scope !== scope) continue;
    const same = card => record.step_id ? getProductionStepId(card) === record.step_id :
      (!getProductionStepId(card) || !(record.existingSteps || []).includes(getProductionStepId(card))) &&
      Number(card.orderDbId) === record.orderDbId && Number(card.itemId ?? card.orderItemId) === record.itemId &&
      Number(card.decorationId) === record.decorationId &&
      String(card.department || card.claimedDept || record.department).toLowerCase() === String(record.department).toLowerCase();
    const source = next[record.from] || [];
    const existing = source.find(same);
    const index = source.findIndex(same);
    next = { ...next };
    for (const lane of Object.keys(next)) {
      if (lane !== "Archive") next[lane] = next[lane].filter(card => !same(card));
    }
    next[record.from] = [...next[record.from]];
    next[record.from].splice(index >= 0 ? index : next[record.from].length, 0, existing || record.sourceCard);
  }
  return next;
}

export function productionMutationError(response, payload) {
  const error = new Error(payload?.message || `HTTP ${response.status}`);
  // Gateway errors/network losses cannot prove that the transaction rolled back.
  error.definite = payload?.committed !== true && ((response.status >= 400 && response.status < 500 && response.status !== 408) || payload?.committed === false);
  return error;
}

const cardIdentity = card => getProductionStepId(card) ? `step:${getProductionStepId(card)}` : `planned:${getProductionScheduleKey(card)}`;
const rollbackTag = "__productionMutation";

export function captureProductionCardRollback(columns, card, scope) {
  const identity = cardIdentity(card);
  const lane = Object.keys(columns).find(key => columns[key].some(row => cardIdentity(row) === identity));
  if (!lane) return null;
  const index = columns[lane].findIndex(row => cardIdentity(row) === identity);
  return { identity, lane, index, card: { ...columns[lane][index] }, scope,
    before: columns[lane][index + 1] && cardIdentity(columns[lane][index + 1]),
    after: columns[lane][index - 1] && cardIdentity(columns[lane][index - 1]),
    token: globalThis.crypto.getRandomValues(new Uint32Array(4)).join('-'), committed: false };
}

export function tagProductionCardMutation(columns, operation) {
  if (!operation) return columns;
  return Object.fromEntries(Object.entries(columns).map(([lane, rows]) => [lane, rows.map(card =>
    cardIdentity(card) === operation.identity ? { ...card, [rollbackTag]: operation.token } : card)]));
}

export function canApplyProductionCardMutation(columns, operation, scope) {
  if (!operation) return true;
  if (operation.scope !== scope) return false;
  const matching = Object.values(columns).flat().filter(card => cardIdentity(card) === operation.identity);
  return matching.length === 1 && (matching[0][rollbackTag] === operation.token ||
    JSON.stringify(matching[0]) === JSON.stringify(operation.card));
}

export function rollbackProductionCard(columns, operation, scope) {
  if (!operation || operation.committed || operation.scope !== scope) return columns;
  // A fresh server row, a removal, or a newer operation wins over this rollback.
  if (!ownsProductionCardMutation(columns, operation, scope)) return columns;
  const next = Object.fromEntries(Object.entries(columns).map(([lane, rows]) =>
    [lane, rows.some(card => cardIdentity(card) === operation.identity)
      ? rows.filter(card => cardIdentity(card) !== operation.identity) : rows]));
  const rows = [...(next[operation.lane] || [])];
  const before = rows.findIndex(card => cardIdentity(card) === operation.before);
  const after = rows.findIndex(card => cardIdentity(card) === operation.after);
  rows.splice(before >= 0 ? before : after >= 0 ? after + 1 : Math.min(operation.index, rows.length), 0, operation.card);
  return { ...next, [operation.lane]: rows };
}

export function ownsProductionCardMutation(columns, operation, scope) {
  if (!operation || operation.scope !== scope) return false;
  const matching = Object.values(columns).flat().filter(card => cardIdentity(card) === operation.identity);
  return matching.length > 0 && matching.every(card => card[rollbackTag] === operation.token);
}
